import React, { useEffect, useRef, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl from "mapbox-gl";
import {
  FullscreenControl,
  GeolocateControl,
  NavigationControl,
  MapboxGL,
} from "mapbox-gl";
import { useDispatch, useSelector } from "react-redux";
import { getL1SheetProperty } from "../../../features/reduxworkbook/WorkbookSlice";
import { buildQuery } from "../../../features/reduxworkbook/utils/buildQuery";
import { useGetDataMutation } from "../../../features/reduxworkbook/dataApiSlice";
import {
  buildLatLongFilterReduxObject,
  deriveLatLongColumnName,
} from "./buildlatLongreduxFilter";
import debounce from "lodash.debounce";
import { setSheetControls } from "../../../features/reduxworkbook/WorkbookControllingSlice";
import {
  getColourScemeOptions,
  getDefaultColourScheme,
} from "../../../Colors/ColorPalettes";

const CordinateMap = ({
  Data,
  Sheetid,
  reduxKey,
  vizType,
  dataSourceID,
  isSheetInDashboard,
  workbookType,
  widthHeightObject,
}) => {
  const availableAreas = [];
  // const measureKey = valueKeys[0]?.responseKey;
  const [mapBounds, setMapBounds] = useState(null);
  // const measure = valueKeys[0]?.measure;
  const mapContainerRef = useRef(null);
  const mapObjectRef = useRef(null);
  const popup = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
  });

  const dispatch = useDispatch();
  const customization = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "customization")
  );
  const reduxQuery = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "query")
  );
  const { rows, groupByFields, filters: reduxFilterArray } = reduxQuery;

  const [getData, { data: queryData, isLoading, isSuccess, isError, error }] =
    useGetDataMutation();
  useEffect(() => {
    if (Sheetid) {
      dispatch(
        setSheetControls({
          Sheetid: Sheetid,
          controls: {
            isPause: true,
          },
        })
      );
    }
  }, []);

  const { conditionalFormatting, dashboardBoxSize } = customization;
  let width = "100%",
    height = "100%";
  if (isSheetInDashboard) {
    if (workbookType === "draft" && dashboardBoxSize) {
      console.log("ran");
      width = dashboardBoxSize.width;
      height = dashboardBoxSize.height;
    }
    if (workbookType === "published" && widthHeightObject) {
      console.log("ran");
      width = widthHeightObject.width;
      height = widthHeightObject.height;
    }
  }

  const conditionalFormattingItem =
    conditionalFormatting.length > 0 ? conditionalFormatting[0] : null;
  const colorSchemes = getColourScemeOptions("RANDOM");
  const colourObject = colorSchemes.find(
    (obj) => obj.id === conditionalFormattingItem?.colorScale
  );
  const defaultColors = getDefaultColourScheme("RANDOM");

  const COLORS =
    colourObject?.colors ?? defaultColors !== undefined
      ? defaultColors["colourSceme"]["colors"]
      : [];
  useEffect(() => {
    mapboxgl.accessToken =
      "pk.eyJ1Ijoia2hhbmRhbmlzaCIsImEiOiJjbHVqYmJ0cmkwZGVxMnBwaHlrbWF6cG8xIn0.YNX48zrv4Gim-H2GmoZqPA"; // Replace with your Mapbox access token
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/light-v10",
      center: [78.9629, 23.5937], // Center of India
      zoom: 4,
    });
    // Ensure availableAreas is defined and has elements before calling forEach
    if (availableAreas && Array.isArray(availableAreas)) {
      availableAreas.forEach((City) => {
        const [longitude, latitude] = City.coordinates; // Get coordinates from the city data
        new MapboxGL.Marker()
          .setLngLat([longitude, latitude])
          .setPopup(new MapboxGL.Popup().setHTML(`<h3 >${City.name}</h3>`))
          .addTo(map);
      });
    }

    const updateBounds = debounce(() => {
      if (map) {
        const mapBounds = map.getBounds().toArray();
        console.log("Map Bounds:", mapBounds); // Debugging map bounds
        setMapBounds(mapBounds);
      }
    }, 1200); // Debounce time in ms

    map.addControl(new FullscreenControl(), "top-left");
    map.addControl(
      new GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      })
    );
    map.addControl(new NavigationControl(), "top-left");
    //update coordinates in redux

    map.on("load", () => {
      map.addSource("points-source", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      // ✅ Add a symbol layer for points
      map.addLayer({
        id: "points-layer",
        type: "circle", // Use "circle" type instead of "symbol" for color customization
        source: "points-source",
        paint: {
          "circle-radius": 4, // Adjust size if needed
          "circle-color": "#172554",
          // "circle-color": [
          //   "match",
          //   ["get", "Direct Sales Zone Text"],
          //   "North",
          //   "#fbb03b",
          //   "South",
          //   "#223b53",
          //   "East",
          //   "#e55e5e",
          //   "West",
          //   "#3bb2d0",
          //   "#172554",
          // ], // Blue color
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff", // White outline
        },
      });

      // ✅ Add an empty GeoJSON source for states
      map.addSource("states-source", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });

      // ✅ Add a fill layer for state coloring
      map.addLayer({
        id: "states-layer",
        type: "fill",
        source: "states-source",
        paint: {
          "fill-color": ["get", "color"], // Dynamic color
          "fill-opacity": 0.6,
        },
      });

      updateBounds();
    });

    map.on("moveend", updateBounds); // Triggers when movement stops

    map.on("mousemove", "points-layer", (e) => {
      map.getCanvas().style.cursor = "pointer";

      const coordinates = e.features[0].geometry.coordinates.slice();
      const properties = e.features[0].properties;

      console.log(properties);

      // Dynamically generate styled HTML content
      const propertiesHTML = Object.entries(properties)
        .map(([key, value]) => `<div><strong>${key}:</strong> ${value}</div>`)
        .join("");

      popup
        .setLngLat(coordinates)
        .setHTML(
          `<div class="custom-tooltip">
            <div class="custom-tooltip-content">
              ${propertiesHTML}
            </div>
          </div>
          `
        )
        .addTo(map);
    });

    // Remove popup when mouse leaves
    map.on("mouseleave", "points-layer", () => {
      map.getCanvas().style.cursor = "";
      popup.remove();
    });

    map.on("mouseleave", "points-layer", () => {
      map.getCanvas().style.cursor = "";
      popup.remove();
    });

    mapObjectRef.current = map;

    map.on("moveend", updateBounds); // Triggers when movement stops

    map.on("mousemove", "points-layer", (e) => {
      map.getCanvas().style.cursor = "pointer";

      const coordinates = e.features[0].geometry.coordinates.slice();
      const properties = e.features[0].properties;

      console.log(properties);

      // Dynamically generate styled HTML content
      const propertiesHTML = Object.entries(properties)
        .filter(([key]) => key.toLowerCase() !== "color") // Remove 'color' key
        .map(([key, value]) => `<div><strong>${key}:</strong> ${value}</div>`)
        .join("");

      popup
        .setLngLat(coordinates)
        .setHTML(
          `<div class="custom-tooltip">
            <div class="custom-tooltip-content">
              ${propertiesHTML}
            </div>
          </div>
          `
        )
        .addTo(map);
    });

    // Remove popup when mouse leaves
    map.on("mouseleave", "points-layer", () => {
      map.getCanvas().style.cursor = "";
      popup.remove();
    });

    map.on("mouseleave", "points-layer", () => {
      map.getCanvas().style.cursor = "";
      popup.remove();
    });

    mapObjectRef.current = map;

    return () => map.remove();
  }, []);
  console.log(rows);

  useEffect(() => {
    console.log(reduxFilterArray);

    const updatedFilterArray = buildLatLongFilterReduxObject(
      mapBounds,
      groupByFields,
      reduxFilterArray
    );
    console.log(updatedFilterArray);

    const queryObject = {
      filters: updatedFilterArray,
      rows: rows,
      groupByFields: groupByFields,
      Sheetid: Sheetid,
    };

    const { queryBody } = buildQuery(
      queryObject,
      dataSourceID,
      [],
      [],
      700,
      vizType
    );
    console.log(queryBody);

    getData(queryBody);
  }, [mapBounds, rows, groupByFields, reduxFilterArray]);

  useEffect(() => {
    const { latColumnName, longColumnName } =
      deriveLatLongColumnName(groupByFields);
    console.log("Query Data:", queryData);
    console.log("Rows:", rows);
    if (queryData && mapObjectRef?.current) {
      console.log("Query Data:", queryData);
      console.log("Rows:", rows);

      const map = mapObjectRef.current;

      const pointGeoJSON = {
        type: "FeatureCollection",
        features: queryData.map((row) => {
          // Dynamically create properties object
          const properties =
            rows.length > 0
              ? rows.reduce((acc, column) => {
                  let key;
                  if (column.dataType === "Date") {
                    key = `${column.rename}${column["dateFormat"]["value"]}`;
                  }
                  if (column.dataType === "String") {
                    key = column.rename;
                  }
                  if (column.dataType === "Number") {
                    key = column.rename;
                  }
                  if (key in row) {
                    acc[column.rename] = row[key]; // Assign value if key exists in queryData
                  }
                  return acc;
                }, {})
              : {};
          console.log(properties);

          return {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [
                Number(row[longColumnName]), // Longitude
                Number(row[latColumnName]), // Latitude
              ],
            },
            properties,
          };
        }),
      };

      // ✅ Update the points source if it exists
      if (map.getSource("points-source")) {
        console.log("Map source found, updating data...");
        console.log(pointGeoJSON);
        map.getSource("points-source").setData(pointGeoJSON);
      }
    }
  }, [queryData, groupByFields, rows]);
  // Runs whenever new data is received

  return (
    <div className="w-full h-full relative">
      <div
        ref={mapContainerRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height,
          width,
        }}
      />
    </div>
  );
};

export default CordinateMap;
